import { useState, useEffect } from 'react';
import { videoGallery } from '../../../mocks/videoGallery';

export default function VideoGallery() {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('videos');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const handleVideoClick = (index: number) => {
    const video = videoGallery[index];
    if (video.isExternalLink) {
      window.open(video.videoUrl, '_blank', 'noopener,noreferrer');
    } else {
      setSelectedVideo(index);
    }
  };

  return (
    <section id="videos" className="relative py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className={`mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-3">
            <span className="inline-block text-xs font-semibold tracking-[0.25em] uppercase text-[#800000] mb-4 px-6 py-2 border border-[#800000]/20 bg-[#800000]/5" style={{ fontFamily: "'Inter', sans-serif" }}>Media</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-black text-center mb-4 tracking-tight" style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}>
            Video Gallery
          </h2>
          <p className="text-base text-center text-gray-600 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Watch highlights from performances, presentations, and community initiatives
          </p>
        </div>

        {/* Video Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {videoGallery.map((video, index) => (
            <div
              key={index}
              className={`bg-white group cursor-pointer transition-all duration-500 border border-gray-200 hover:border-[#800000]/30 hover:shadow-lg ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onClick={() => handleVideoClick(index)}
            >
              <div className="relative overflow-hidden h-72 bg-gray-100">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover object-center transition-all duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-all duration-300"></div>
                
                {/* Play Button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 flex items-center justify-center bg-[#800000] text-white transition-all duration-300 group-hover:scale-110 group-hover:bg-black">
                    <i className={`${video.isExternalLink ? 'ri-external-link-line' : 'ri-play-fill'} text-4xl w-8 h-8 flex items-center justify-center`}></i>
                  </div>
                </div>

                {/* Duration Badge */}
                <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/80 text-white text-xs font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {video.duration}
                </div>
              </div>

              {/* Video Info */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-black mb-2 tracking-tight group-hover:text-[#800000] transition-colors duration-300" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {video.title}
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif", lineHeight: '1.7' }}>
                  {video.description}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <span className="text-xs font-semibold text-[#800000] uppercase tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {video.category}
                  </span>
                  <span className="text-xs text-gray-500 font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {video.date}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Video Modal */}
        {selectedVideo !== null && !videoGallery[selectedVideo].isExternalLink && (
          <div 
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-6"
            onClick={() => setSelectedVideo(null)}
          >
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl w-6 h-6 flex items-center justify-center"></i>
            </button>
            
            <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={videoGallery[selectedVideo].videoUrl}
                  title={videoGallery[selectedVideo].title}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="mt-6 text-center">
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {videoGallery[selectedVideo].title}
                </h3>
                <p className="text-base text-white/80" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {videoGallery[selectedVideo].description}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
